/**
 * ErrorBoundary global (29/05/2026) — captura throws em componentes filhos
 * pra não derrubar a página inteira em "tela preta".
 *
 * Causas históricas que mordeu antes deste ErrorBoundary existir:
 *  - useMemo/useCallback usado sem import (ReferenceError em runtime)
 *  - hooks chamados depois de early return (Rendered fewer hooks)
 *  - prop undefined que vira .map() crash
 *
 * UX do fallback:
 *  - Mensagem amigável + tecla pra ver detalhes técnicos
 *  - Botão "Recarregar" (reload da página) e "Voltar pra home" (hash → dashboard)
 *  - Stack copiável (assist o devtools que muitos operadores não abrem)
 *
 * O ErrorBoundary fica em volta do <App/>, então qualquer erro abaixo dele
 * cai aqui. Não captura erros em useEffect async / event handlers (limitação
 * do React) — esses continuam indo pro console (e o user, sem feedback).
 */
import { Component, type ReactNode, type ErrorInfo } from 'react';
import { isChunkLoadError, reloadWhenReady } from './lib/deployGuard';

type Props = { children: ReactNode };
type State = { error: Error | null; info: ErrorInfo | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ info });
    // Loga no console pra debug — não envia em rede pra não criar
    // dependência circular se o erro for justamente no fetch.
    console.error('[ErrorBoundary] uncaught error in render tree', error, info);
    // Chunk de um build que já saiu do ar (aba aberta durante o deploy): recarrega sozinho.
    if (isChunkLoadError(error)) reloadWhenReady(`chunk: ${error.message}`);
  }

  render() {
    if (!this.state.error) return this.props.children;

    const err = this.state.error;
    if (isChunkLoadError(err)) {
      return (
        <div className="min-h-screen bg-slate-950 text-slate-300 flex items-center justify-center p-6 text-sm">
          ↻ Nova versão do NMS publicada — recarregando…
        </div>
      );
    }
    const stack = (err.stack ?? '').slice(0, 2000);
    const componentStack = (this.state.info?.componentStack ?? '').slice(0, 1500);

    return (
      <div className="min-h-screen bg-slate-950 text-slate-200 flex items-center justify-center p-6">
        <div className="max-w-2xl w-full bg-slate-900 ring-1 ring-rose-800/60 rounded-lg p-6 space-y-4">
          <h1 className="text-xl font-semibold text-rose-300">⚠ Erro inesperado na interface</h1>
          <p className="text-sm text-slate-300">
            Algo deu errado renderizando esta tela. A coleta (workers, polling do Zabbix, syslog) <strong>continua rodando normalmente</strong> em segundo plano — só a UI quebrou.
          </p>

          <div className="text-xs bg-slate-950 ring-1 ring-slate-800 rounded p-3 font-mono space-y-2">
            <div>
              <span className="text-slate-500">tipo:</span>{' '}
              <span className="text-rose-400">{err.name}</span>
            </div>
            <div>
              <span className="text-slate-500">mensagem:</span>{' '}
              <span className="text-slate-200 break-all">{err.message}</span>
            </div>
          </div>

          <details className="text-xs">
            <summary className="cursor-pointer text-slate-400 hover:text-slate-200 select-none">
              ▸ detalhes técnicos (cola no canal do dev se quiser ajuda)
            </summary>
            <pre className="mt-2 bg-slate-950 ring-1 ring-slate-800 rounded p-3 text-[10px] text-slate-400 overflow-auto max-h-64 whitespace-pre-wrap">
{stack}
{componentStack && '\n\nComponent stack:' + componentStack}
            </pre>
          </details>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button onClick={() => { window.location.hash = ''; window.location.reload(); }}
                    className="px-3 py-1.5 text-sm text-slate-400 hover:text-slate-200">
              ← voltar pra home
            </button>
            <button onClick={() => window.location.reload()}
                    className="px-3 py-1.5 text-sm bg-emerald-700 hover:bg-emerald-600 rounded">
              ↻ Recarregar
            </button>
          </div>
        </div>
      </div>
    );
  }
}
